import { applyMinimum, hoursToMinutes, MINUTES_PER_HOUR } from '@portal/shared';

const HOURS = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const COUNT = new Intl.NumberFormat();
const DAY = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' });
const DAY_TIME = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatHours(hours: number): string {
  return HOURS.format(hours);
}

/**
 * A duration with its units attached: `2 h`, `1 h 20 m`, `45 m`.
 *
 * Time logged is tracked to the minute, and a decimal hour is the wrong way to show it to
 * anybody: twenty minutes is `0.3 h` at the precision `formatHours` uses, which reads as a
 * rounding artefact rather than as a figure somebody recorded, and `0.33` reads as arithmetic
 * the reader has to finish. Callers pass hours — the unit of record everywhere — and this is
 * the only place that turns them into minutes for a person.
 *
 * Unlike `formatHours` this INCLUDES the unit, so call sites must not append their own ` h`.
 */
export function formatDuration(hours: number): string {
  const total = hoursToMinutes(hours);
  if (total === 0) return '0 h';

  const sign = total < 0 ? '-' : '';
  const whole = Math.abs(total);
  const h = Math.floor(whole / MINUTES_PER_HOUR);
  const m = whole % MINUTES_PER_HOUR;

  if (h === 0) return `${sign}${formatCount(m)} m`;
  if (m === 0) return `${sign}${formatCount(h)} h`;
  return `${sign}${formatCount(h)} h ${formatCount(m)} m`;
}

/**
 * The same duration as something a person can EDIT and this app can read back.
 *
 * `formatDuration` is for reading — spaced, with the units set apart. This is the seed value of
 * the text box, so it has to round-trip through `parseDuration` unchanged: `1h 20m`, `20m`,
 * `2h`. Empty for a request nobody has logged time against, because `0h` in the box is a
 * claim that the work took no time.
 */
export function durationValue(hours: number | null): string {
  if (hours === null) return '';
  const total = hoursToMinutes(hours);
  const h = Math.floor(total / MINUTES_PER_HOUR);
  const m = total % MINUTES_PER_HOUR;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** What the duration box accepts, said once so the two forms that use it cannot disagree. */
export const DURATION_HINT = 'Hours, or minutes with a unit — 1.5, 90m, 1h 30m, 1:30.';

/**
 * "1 h 20 m billable." — or, when the one-hour minimum moved the number, both figures:
 * "20 m logged, 1 h billable."
 *
 * Naming the logged figure only when it differs is the point. Below an hour the reader has to
 * see both to check the bill against the time; at or above it they are the same number, and
 * printing it twice reads as two facts rather than one. Shared by the two forms that log time
 * so the sentence cannot drift between them.
 */
export function billableSentence(hours: number): string {
  const billable = applyMinimum(hours);
  if (billable === hours) return `${formatDuration(billable)} billable.`;
  return `${formatDuration(hours)} logged, ${formatDuration(billable)} billable.`;
}

/**
 * What a request is BILLED, as a duration. The one-hour minimum applies to anything that was
 * logged at all; a request nobody logged time against is not billed a floor it never earned.
 */
export function billedDuration(actualHours: number | null): string | null {
  return actualHours === null ? null : formatDuration(applyMinimum(actualHours));
}

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/**
 * A file size, at the precision a person deciding whether to click actually needs.
 *
 * KB and MB against 1024, which is what every file manager the client has ever used shows and
 * what the 250 MB ceiling is measured in — `MAX_ATTACHMENT_BYTES` is 250 × 1024². Quoting a
 * decimal megabyte here would print "262.1 MB" for the ceiling itself.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatCount(bytes)} B`;
  if (bytes < 1024 * 1024) return `${formatCount(Math.round(bytes / 1024))} KB`;
  // A trailing `.0` is dropped so the ceiling prints as "250 MB" — the number the client was
  // promised in the form — rather than as "250.0 MB", which reads like a different figure.
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

export function formatDay(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : DAY.format(date);
}

export function formatDayTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : DAY_TIME.format(date);
}

/**
 * The estimate as the client sees it, already floored by the shared `applyMinimum`. A raw
 * "0.5 h" would contradict the minimum-hours notice sitting beside it.
 */
export function estimateLabel(estimatedHours: number | null): string {
  if (estimatedHours === null) return 'not estimated yet';
  return formatDuration(applyMinimum(estimatedHours));
}

/**
 * How long ago, in whole days.
 *
 * A staff question is cleared by the client answering, so one that nobody has answered stays
 * lit indefinitely — a client who replied by phone leaves it up. Printing the AGE is what makes
 * that read as stale rather than as silence: "14 days ago" beside the question is the prompt to
 * chase it, where a bare date is just a date.
 */
export function sinceLabel(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${formatCount(days)} days ago`;
}

/**
 * What a SELECTION of requests will be billed, floored per issue — never summed then floored.
 *
 * The one-hour minimum is a floor on a REQUEST, not on an invoice: "Every request is billed at
 * a 1-hour minimum". Fifty requests estimated at half an hour each are billed 50 x 1 = 50 h.
 * Summed raw and floored once, `applyMinimum(25)` is 25 h — a 25-hour UNDERCOUNT, which is the
 * direction that produces an invoice LARGER than the quote the client approved against, in a
 * product whose whole purpose is billing transparency. Sum-then-floor also becomes a silent
 * no-op above one hour, so it looks correct in every small case and misprices only at the scale
 * this feature exists for.
 *
 * `?? 0` before the floor, so an unestimated request counts as one hour rather than as nothing —
 * it still bills at least the minimum. That is not free to assume on the reader's behalf: any
 * caller showing this total must also say how many rows were unestimated. See
 * `unestimatedCount`.
 */
export function totalEstimateHours(
  issues: ReadonlyArray<{ estimatedHours: number | null }>,
): number {
  return issues.reduce((sum, issue) => sum + applyMinimum(issue.estimatedHours ?? 0), 0);
}

/** How many of them carry no estimate, so a total can name where the floor did the work. */
export function unestimatedCount(issues: ReadonlyArray<{ estimatedHours: number | null }>): number {
  return issues.filter((issue) => issue.estimatedHours === null).length;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return '?';
  return parts.map((part) => (part[0] ?? '').toUpperCase()).join('');
}

export function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.round((part / whole) * 100);
}
